import type { ComputedRef, Ref } from 'vue';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import type { IApiFormErrors } from '@modules/core/services/query/types';
import { AFFILIATES_APPROVED_PERMISSIONS } from '@modules/affiliates-approved/constants';
import {
  AFFILIATES_PENDING_APPROVE_ROUTE_NAME,
  AFFILIATES_PENDING_PERMISSIONS,
  TRANSLATION_GROUP_AFFILIATES_PENDING,
} from '@modules/affiliates-pending/constants';
import {
  usePendingAffiliatesApproveMutation,
  usePendingAffiliatesRejectMutation,
} from '@modules/affiliates-pending/mutations';
import { AFFILIATES_REQUEST_HISTORY_REAPPROVE_ROUTE_NAME, TRANSLATION_GROUP_AFFILIATES_REQUEST_HISTORY } from '@modules/affiliates-request-history/constants';
import { useChangeAffiliatesStatusRequestHistoryMutation } from '@modules/affiliates-request-history/mutations';
import { useConfirmDialogs } from '@modules/core/composables/dialogs';
import { useI18n } from '@modules/i18n/composables';
import { usePermissionsStore } from '@modules/permissions/store';

import type { IAffiliatesApprovePayload } from '../types';

export interface AffiliatePendingRequestComposable {
  selectManagerPermission: ComputedRef<boolean>;
  selectDepartmentPermission: ComputedRef<boolean>;
  isApproveLoading: Ref<boolean>;
  isReapproveLoading: Ref<boolean>;
  approveErrors: Ref<IApiFormErrors | undefined>;
  reapproveErrors: Ref<IApiFormErrors | undefined>;
  clearApproveError(key: string): void;
  clearReapproveError(key: string): void;
  deny(id: number, event?: MouseEvent, callback?: () => (void | Promise<void>)): void;
  approve(id: number, event?: MouseEvent, callback?: () => (void | Promise<void>)): void;
  reapprove(id: number, event?: MouseEvent, callback?: () => (void | Promise<void>)): void;
  confirmApprove(id: number, values?: IAffiliatesApprovePayload, event?: MouseEvent, callback?: () => (void | Promise<void>)): void;
  confirmReapprove(id: number, values?: IAffiliatesApprovePayload, event?: MouseEvent, callback?: () => (void | Promise<void>)): void;
}

export default function useAffiliateChangeStatus(): AffiliatePendingRequestComposable {
  const { mutateAsync: reject } = usePendingAffiliatesRejectMutation();
  const {
    mutateAsync: accept,
    isLoading: isApproveLoading,
    errors: approveErrors,
    clearError: clearApproveError,
  } = usePendingAffiliatesApproveMutation();

  const {
    mutateAsync: reaccept,
    isLoading: isReapproveLoading,
    errors: reapproveErrors,
    clearError: clearReapproveError,
  } = useChangeAffiliatesStatusRequestHistoryMutation();

  const router = useRouter();
  const { confirmDelete, confirmEdit } = useConfirmDialogs();
  const { getTranslation } = useI18n();
  const { hasPermission } = usePermissionsStore();

  const selectManagerPermission = computed<boolean>(() => hasPermission(AFFILIATES_PENDING_PERMISSIONS.SELECT_MANAGER).value);

  const selectDepartmentPermission = computed<boolean>(() => hasPermission(AFFILIATES_APPROVED_PERMISSIONS.UPDATE_DEPARTMENT_FOR_AFFILIATES).value);

  function confirmApprove(id: number, values?: IAffiliatesApprovePayload, event?: MouseEvent, callback?: () => (void | Promise<void>)): void {
    confirmEdit({
      event,
      header: getTranslation(TRANSLATION_GROUP_AFFILIATES_PENDING, 'ACCEPT_MODAL_TITLE').value,
      message: getTranslation(TRANSLATION_GROUP_AFFILIATES_PENDING, 'ACCEPT_MODAL_QUESTION').value,
      acceptLabel: getTranslation(TRANSLATION_GROUP_AFFILIATES_PENDING, 'ACCEPT_MODAL_YES').value,
      rejectLabel: getTranslation(TRANSLATION_GROUP_AFFILIATES_PENDING, 'ACCEPT_MODAL_NO').value,
      accept: () => {
        void accept({ ...values, id }).then(() => {
          void callback?.();
        });
      },
    });
  }

  function approve(id: number, event?: MouseEvent, callback?: () => (void | Promise<void>)): void {
    if (selectManagerPermission.value || selectDepartmentPermission.value) {
      void router.push({
        name: AFFILIATES_PENDING_APPROVE_ROUTE_NAME,
        params: {
          id,
        },
      });
    } else {
      confirmApprove(id, undefined, event, callback);
    }
  }

  function confirmReapprove(id: number, values?: IAffiliatesApprovePayload, event?: MouseEvent, callback?: () => (void | Promise<void>)): void {
    confirmEdit({
      event,
      header: getTranslation(TRANSLATION_GROUP_AFFILIATES_REQUEST_HISTORY, 'CHANGE_TITLE').value,
      message: getTranslation(TRANSLATION_GROUP_AFFILIATES_REQUEST_HISTORY, 'CHANGE_QUESTION').value,
      acceptLabel: getTranslation(TRANSLATION_GROUP_AFFILIATES_REQUEST_HISTORY, 'CHANGE_YES').value,
      rejectLabel: getTranslation(TRANSLATION_GROUP_AFFILIATES_REQUEST_HISTORY, 'CHANGE_NO').value,
      accept: () => {
        void reaccept({ ...values, id }).then(() => {
          void callback?.();
        });
      },
    });
  }

  function reapprove(id: number, event?: MouseEvent, callback?: () => (void | Promise<void>)): void {
    if (selectManagerPermission.value || selectDepartmentPermission.value) {
      void router.push({
        name: AFFILIATES_REQUEST_HISTORY_REAPPROVE_ROUTE_NAME,
        params: {
          id,
        },
      });
    } else {
      confirmReapprove(id, undefined, event, callback);
    }
  }

  function deny(id: number, event?: MouseEvent, callback?: () => (void | Promise<void>)): void {
    confirmDelete({
      event,
      header: getTranslation(TRANSLATION_GROUP_AFFILIATES_PENDING, 'REJECT_TITLE').value,
      message: getTranslation(TRANSLATION_GROUP_AFFILIATES_PENDING, 'REJECT_QUESTION').value,
      acceptLabel: getTranslation(TRANSLATION_GROUP_AFFILIATES_PENDING, 'REJECT_YES').value,
      rejectLabel: getTranslation(TRANSLATION_GROUP_AFFILIATES_PENDING, 'REJECT_NO').value,
      accept: () => {
        void reject(id).then(() => {
          void callback?.();
        });
      },
    });
  }

  return {
    selectManagerPermission,
    selectDepartmentPermission,
    isApproveLoading,
    isReapproveLoading,
    approveErrors,
    reapproveErrors,
    clearApproveError,
    clearReapproveError,
    approve,
    reapprove,
    deny,
    confirmApprove,
    confirmReapprove,
  };
}
